import React, { useMemo, useState, useEffect } from 'react';
import { MapContainer, TileLayer, CircleMarker, Popup } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { cn } from '../../utils/helpers';
import { COLORS } from '../../utils/theme';
import SubmissionForm from './SubmissionForm';
import { submissionService } from '../../services/submissionService';

// Initialize Leaflet icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

const MapView = ({ data }) => {
  const [isSubmissionOpen, setIsSubmissionOpen] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);
  
  // Center on Scotland
  const center = [56.4907, -4.2026];

  // Fetch pending submissions count
  useEffect(() => {
    const fetchPendingCount = async () => {
      const submissions = await submissionService.getSubmissions();
      setPendingCount(submissions.length);
    };
    fetchPendingCount();
  }, []);

  // Process data for visualization
  const mapData = useMemo(() => {
    const processedData = new Map();

    data.forEach(item => {
      if (item.lat && item.lng && !isNaN(item.lat) && !isNaN(item.lng)) {
        const key = `${item.lat}-${item.lng}`;
        if (!processedData.has(key)) {
          processedData.set(key, {
            institution: item.institution,
            position: [parseFloat(item.lat), parseFloat(item.lng)],
            outputs: []
          });
        }
        processedData.get(key).outputs.push(item);
      }
    });

    return Array.from(processedData.values());
  }, [data]);

  // Calculate marker size based on number of outputs
  const getMarkerSize = (numOutputs) => {
    return Math.min(Math.max(numOutputs * 3, 10), 30); // Min 10px, max 30px
  };

  return (
    <div className="space-y-4">
      {/* Header with Submit Button */}
      <div className="bg-white p-4 rounded-lg shadow">
        <div className="flex justify-between items-center">
          <div>
            <h3 className="text-lg font-semibold mb-2">Research Output Locations</h3>
            <p className="text-sm text-gray-600">
              Showing {mapData.length} institutions with geographical data
            </p>
          </div>
          <div className="flex items-center space-x-4">
            {pendingCount > 0 && (
              <div className="text-sm text-gray-600">
                {pendingCount} pending submission{pendingCount !== 1 ? 's' : ''}
              </div>
            )}
            <button
              onClick={() => setIsSubmissionOpen(true)}
              className="px-4 py-2 rounded text-white transition-colors hover:opacity-90"
              style={{ backgroundColor: COLORS.primary.main }}
            >
              Submit New Research
            </button>
          </div>
        </div>
      </div>

      {/* Map Container */}
      <div className="h-[600px] w-full rounded-lg overflow-hidden border bg-white">
        <MapContainer
          center={center}
          zoom={7}
          scrollWheelZoom={true}
          style={{ height: '100%', width: '100%' }}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />

          {mapData.map(({ institution, position, outputs }) => (
            <CircleMarker
              key={`${position[0]}-${position[1]}`}
              center={position}
              radius={getMarkerSize(outputs.length)}
              fillColor={COLORS.primary.main}
              color={COLORS.primary.dark}
              weight={1}
              opacity={1}
              fillOpacity={0.6}
            >
              <Popup>
                <div className="max-w-xs">
                  <h3 className="font-semibold text-lg mb-2">{institution}</h3>
                  <p className="text-sm text-gray-600 mb-2">
                    Total outputs: {outputs.length}
                  </p>
                  <div className="max-h-40 overflow-y-auto">
                    {outputs.slice(0, 5).map((output, idx) => (
                      <div
                        key={idx}
                        className={cn(
                          "py-2",
                          idx !== outputs.length - 1 && "border-b border-gray-200"
                        )}
                      >
                        <p className="text-sm font-medium">{output.project}</p>
                        <p className="text-xs text-gray-500">
                          {output.outputType} ({output.year})
                        </p>
                        <p className="text-xs text-gray-500">
                          {output.surname}, {output.firstName}
                        </p>
                      </div>
                    ))}
                    {outputs.length > 5 && (
                      <p className="text-xs text-gray-500 mt-2">
                        And {outputs.length - 5} more outputs...
                      </p>
                    )}
                  </div>
                </div>
              </Popup>
            </CircleMarker>
          ))}
        </MapContainer>
      </div>

      {/* Legend */}
      <div className="bg-white p-4 rounded-lg shadow">
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2">
            <div 
              className="w-4 h-4 rounded-full opacity-60"
              style={{ backgroundColor: COLORS.primary.main }}
            ></div>
            <span className="text-sm">Institution Location</span>
          </div>
          <div className="text-sm text-gray-600">
            • Circle size indicates number of outputs
          </div>
        </div>
      </div>

      {/* Submission Form Modal */}
      <SubmissionForm 
        isOpen={isSubmissionOpen}
        onClose={() => {
          setIsSubmissionOpen(false);
          // Refresh pending count after submission
          submissionService.getSubmissions().then(subs => setPendingCount(subs.length));
        }}
      />
    </div>
  );
};

export default MapView;